import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import SettingsMenu from '../SettingsMenu';
import NotificationCenter from './NotificationCenter';
import { useGame } from '../../context/GameContext';
import { useGameSpace } from '../../context/GameSpaceContext';
import { useAuth } from '../../context/AuthContext';
import logo from '../../images/itclarifies_logo_v2.svg';

export default function Header() {
  
  const { currentSession } = useGame();
  const { gameSpaces, selectedSpaceId } = useGameSpace();
  const { organizationName, organizationLogoUrl } = useAuth();
  const [spaceName, setSpaceName] = useState('Loading...');
  const [logoBroken, setLogoBroken] = useState(false);

  useEffect(() => {
    setLogoBroken(false);
  }, [organizationLogoUrl]);

  useEffect(() => {
    if (!selectedSpaceId) {
      if (currentSession && currentSession.space_name) {
        setSpaceName(currentSession.space_name);
      } else {
        setSpaceName('No Game Space');
      }
      return;
    }
    const selected = gameSpaces.find(gs => gs.space_id === selectedSpaceId);
    setSpaceName(selected ? selected.space_name : 'No Game Space');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameSpaces, selectedSpaceId]); // Intentionally removed currentSession dependency to prevent unnecessary re-renders

  const showOrgLogo = Boolean(organizationLogoUrl) && !logoBroken;

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: 'white',
          color: '#424242',
          borderBottom: '2px solid rgba(0, 0, 0, 0.06)',
        }}
      >
        <Toolbar disableGutters>
          <Container maxWidth="lg" sx={{ px: 'var(--page-x)' }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
              {/* Left: Logo — shortcut back to games */}
              <Tooltip title="Go to your games" arrow>
                <IconButton
                  component={Link}
                  to="/game-cards"
                  size="large"
                  edge="start"
                  aria-label="Go to your games"
                  sx={{ mr: 1, color: '#424242', flexShrink: 0 }}
                >
                  <img src={logo} alt="Itclarifies" style={{ height: 38, width: 'auto' }} />
                </IconButton>
              </Tooltip>

              {/* Center: Learnspace */}
              <Box sx={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
                <Typography sx={{ 
                  fontSize: { xs: '0.65rem', sm: '0.75rem' }, 
                  fontStyle: 'italic', 
                  color: '#757575',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  Learnspace
                </Typography>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold', 
                  color: '#212121', 
                  fontSize: { xs: '0.9rem', sm: '1.1rem' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {spaceName}
                </Typography>
              </Box>

              {/* Right: Organization + Settings */}
              <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 2 }} sx={{ flexShrink: 0 }}>
                <Box sx={{ 
                  display: { xs: 'none', sm: 'flex' }, 
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1,
                }}>
                  {showOrgLogo && (
                    <Box
                      component="img"
                      src={organizationLogoUrl}
                      alt=""
                      onError={() => setLogoBroken(true)}
                      sx={{
                        height: 28,
                        width: 'auto',
                        maxWidth: 72,
                        objectFit: 'contain',
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'flex-end' 
                  }}>
                    <Typography sx={{ 
                      fontSize: '0.7rem', 
                      fontStyle: 'italic', 
                      color: '#9e9e9e' 
                    }}>
                      Organization
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 500, 
                      color: '#757575', 
                      fontSize: '0.85rem',
                      maxWidth: { xs: '120px', sm: '200px' },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {organizationName || 'Guest User'}
                    </Typography>
                  </Box>
                </Box>
                <NotificationCenter />
                <SettingsMenu />
              </Stack>
            </Stack>
          </Container>
        </Toolbar>
      </AppBar>
    </Box>
  );
}
